import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import {
  Box,
  Button,
  Stack,
  Typography,
  TextField,
  MenuItem,
  Paper,
  FormControlLabel,
  Checkbox,
  Grid,
  CircularProgress,
  Container,
  Divider,
  Card,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import IndeterminateCheckBoxIcon from '@mui/icons-material/IndeterminateCheckBox';
import DeleteIcon from '@mui/icons-material/Delete';
import { toast } from 'react-toastify';

import {
  updateStaffMember,
  removeStaffMember,
  getPermissionsCatalog,
} from '../../api/users.api';
import { getStaffMembers } from '../../store/actions/users';
import Page from '../../components/Page';

export default function EditStaffPage() {
  const { id: userId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { staffMembers } = useSelector((state) => state.users);
  const user = staffMembers.find((m) => m?.id === userId || m?._id === userId);

  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [staffRole, setStaffRole] = useState(user?.role || 'support');
  const [staffStatus, setStaffStatus] = useState(user?.status || 'active');

  // Permissions state
  const [catalogGrouped, setCatalogGrouped] = useState({});
  const [rolePermissionsMap, setRolePermissionsMap] = useState({});
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [loadingCatalog, setLoadingCatalog] = useState(true);

  useEffect(() => {
    dispatch(getStaffMembers());
  }, [dispatch]);

  useEffect(() => {
    if (user) {
      setStaffRole(user.role || 'support');
      setStaffStatus(user.status || 'active');
    }
  }, [user]);

  useEffect(() => {
    async function loadCatalog() {
      setLoadingCatalog(true);
      try {
        const res = await getPermissionsCatalog();
        if (res.ok && res.data?.data) {
          const { grouped, catalog, rolePermissions } = res.data.data;

          const finalGrouped = grouped || (catalog || []).reduce((acc, item) => {
            const groupName = item.group || 'General';
            acc[groupName] = acc[groupName] || [];
            acc[groupName].push(item);
            return acc;
          }, {});

          setCatalogGrouped(finalGrouped);
          setRolePermissionsMap(rolePermissions || {});

          // Calculate initial effective permissions for this staff member
          const roleDefaults = rolePermissions?.[user?.role || 'support'] || [];
          const custom = user?.customPermissions || [];
          const denied = user?.deniedPermissions || [];

          if (roleDefaults.includes('*')) {
            const allIds = (catalog || []).map((c) => c.id);
            setSelectedPermissions(allIds);
          } else {
            const merged = new Set([...roleDefaults, ...custom]);
            denied.forEach((p) => merged.delete(p));
            setSelectedPermissions(Array.from(merged));
          }
        }
      } catch (err) {
        console.error('Failed to load permissions catalog', err);
      } finally {
        setLoadingCatalog(false);
      }
    }

    if (userId) {
      loadCatalog();
    }
  }, [userId, user?.role, user?.customPermissions, user?.deniedPermissions]);

  const handleRoleChange = (e) => {
    const newRole = e.target.value;
    setStaffRole(newRole);

    const defaults = rolePermissionsMap[newRole] || [];
    if (defaults.includes('*')) {
      const allIds = Object.values(catalogGrouped).flat().map((item) => item.id);
      setSelectedPermissions(allIds);
    } else {
      setSelectedPermissions(defaults);
    }
  };

  const togglePermission = (permId) => {
    setSelectedPermissions((prev) =>
      prev.includes(permId) ? prev.filter((p) => p !== permId) : [...prev, permId]
    );
  };

  const toggleGroupPermissions = (groupItems) => {
    const groupIds = groupItems.map((i) => i.id);
    const allChecked = groupIds.every((id) => selectedPermissions.includes(id));

    if (allChecked) {
      setSelectedPermissions((prev) => prev.filter((id) => !groupIds.includes(id)));
    } else {
      setSelectedPermissions((prev) => Array.from(new Set([...prev, ...groupIds])));
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setLoading(true);

    const roleDefaults = rolePermissionsMap[staffRole] || [];
    const isWildcardRole = roleDefaults.includes('*');

    let customPermissions = [];
    let deniedPermissions = [];

    if (!isWildcardRole) {
      customPermissions = selectedPermissions.filter((p) => !roleDefaults.includes(p));
      deniedPermissions = roleDefaults.filter((p) => !selectedPermissions.includes(p));
    }

    const updatePayload = {
      role: staffRole,
      status: staffStatus,
      customPermissions,
      deniedPermissions,
    };

    const response = await updateStaffMember(userId, updatePayload);
    setLoading(false);

    if (response.ok) {
      toast.success('Staff member updated successfully');
      dispatch(getStaffMembers());
      navigate('/dashboard/staff');
    } else {
      toast.error(response.data?.message || 'Failed to update staff member');
    }
  };

  const handleRemoveClick = () => {
    setConfirmOpen(true);
  };

  const handleConfirmRemove = async () => {
    setConfirmOpen(false);
    setDeleting(true);
    const response = await removeStaffMember(userId);
    setDeleting(false);

    if (response.ok) {
      toast.success('Staff access revoked successfully');
      dispatch(getStaffMembers());
      navigate('/dashboard/staff');
    } else {
      toast.error(response.data?.message || 'Failed to remove staff member');
    }
  };

  if (!user && !loadingCatalog) {
    return (
      <Page title="Edit Staff Member">
        <Container maxWidth="lg">
          <Stack alignItems="center" py={10}>
            <Typography variant="h5" color="text.secondary">
              Staff member not found or may have been removed.
            </Typography>
            <Button
              variant="contained"
              onClick={() => navigate('/dashboard/staff')}
              sx={{ mt: 3, borderRadius: '8px' }}
            >
              Back to Staff List
            </Button>
          </Stack>
        </Container>
      </Page>
    );
  }

  return (
    <Page title={`Edit Staff: ${user?.username || 'Member'}`}>
      <Container maxWidth="lg">
        {/* Header */}
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 4 }}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <IconButton onClick={() => navigate('/dashboard/staff')} sx={{ bgcolor: '#F4F5F7' }}>
              <ArrowBackIcon />
            </IconButton>
            <div>
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#1E1E2F' }}>
                Edit Staff: {user?.username || user?.email}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {user?.email}
              </Typography>
            </div>
          </Stack>

          <Stack direction="row" spacing={2}>
            <Button
              variant="contained"
              onClick={() => navigate(`/dashboard/audit-logs?actorId=${user?.userId || user?.email || user?.id || user?._id}`)}
              sx={{ borderRadius: '8px', textTransform: 'none', bgcolor: '#4f46e5', '&:hover': { bgcolor: '#4338ca' } }}
            >
              View Activity Log
            </Button>

            <Button
              variant="outlined"
              color="error"
              onClick={handleRemoveClick}
              disabled={deleting}
              startIcon={<DeleteIcon />}
              sx={{ borderRadius: '8px', textTransform: 'none' }}
            >
              {deleting ? 'Removing...' : 'Revoke Staff Access'}
            </Button>
          </Stack>
        </Stack>

        <form onSubmit={handleSave}>
          <Grid container spacing={3}>
            {/* Left Column: Basic Information */}
            <Grid item xs={12} md={5}>
              <Card sx={{ borderRadius: '12px', boxShadow: '0 4px 20px 0 rgba(0,0,0,0.04)', p: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 3 }}>
                  Role & Credentials
                </Typography>

                <Stack spacing={2.5}>
                  <TextField
                    fullWidth
                    id="role"
                    name="role"
                    label="Staff Role"
                    select
                    value={staffRole}
                    onChange={handleRoleChange}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                  >
                    <MenuItem value="admin">Administrator (Full Access)</MenuItem>
                    <MenuItem value="manager">Manager</MenuItem>
                    <MenuItem value="developer">Developer</MenuItem>
                    <MenuItem value="support">Support Agent</MenuItem>
                    <MenuItem value="finance">Finance Specialist</MenuItem>
                  </TextField>

                  <TextField
                    fullWidth
                    id="status"
                    name="status"
                    label="Account Status"
                    select
                    value={staffStatus}
                    onChange={(e) => setStaffStatus(e.target.value)}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                  >
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="pending">Pending Invitation</MenuItem>
                    <MenuItem value="revoked">Revoked / Suspended</MenuItem>
                  </TextField>
                </Stack>
              </Card>
            </Grid>

            {/* Right Column: Access Permissions Matrix */}
            <Grid item xs={12} md={7}>
              <Card sx={{ borderRadius: '12px', boxShadow: '0 4px 20px 0 rgba(0,0,0,0.04)', p: 3 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
                  <div>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      Access Permissions
                    </Typography>

                  </div>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: '#5D5FEF' }}>
                    {selectedPermissions.length} Active
                  </Typography>
                </Stack>

                <Divider sx={{ mb: 3 }} />

                {loadingCatalog ? (
                  <Stack alignItems="center" py={6}>
                    <CircularProgress size={32} />
                  </Stack>
                ) : (
                  <Stack spacing={2.5}>
                    {Object.entries(catalogGrouped).map(([groupName, items]) => {
                      const groupIds = items.map((i) => i.id);
                      const selectedInGroup = groupIds.filter((id) => selectedPermissions.includes(id));
                      const isAllGroupSelected = selectedInGroup.length === groupIds.length;
                      const isSomeGroupSelected = selectedInGroup.length > 0 && !isAllGroupSelected;

                      return (
                        <Paper
                          key={groupName}
                          variant="outlined"
                          sx={{ p: 2, borderRadius: '10px', borderColor: '#EAECEF' }}
                        >
                          <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
                            <Typography
                              variant="subtitle2"
                              sx={{ fontWeight: 700, textTransform: 'uppercase', color: '#5D5FEF', fontSize: '0.8rem' }}
                            >
                              {groupName} ({selectedInGroup.length}/{items.length})
                            </Typography>

                            <Tooltip title={isAllGroupSelected ? 'Deselect Group' : 'Select All in Group'}>
                              <Button
                                size="small"
                                onClick={() => toggleGroupPermissions(items)}
                                startIcon={(() => {
                                  if (isAllGroupSelected) return <CheckBoxIcon color="primary" />;
                                  if (isSomeGroupSelected) return <IndeterminateCheckBoxIcon color="primary" />;
                                  return <CheckBoxOutlineBlankIcon color="action" />;
                                })()}
                                sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                              >
                                {isAllGroupSelected ? 'Deselect All' : 'Select All'}
                              </Button>
                            </Tooltip>
                          </Stack>

                          <Grid container spacing={1.5}>
                            {items.map((item) => {
                              const isChecked = selectedPermissions.includes(item.id);
                              return (
                                <Grid item xs={12} sm={6} key={item.id}>
                                  <Paper
                                    variant="outlined"
                                    onClick={() => togglePermission(item.id)}
                                    sx={{
                                      p: 1.2,
                                      borderRadius: '8px',
                                      cursor: 'pointer',
                                      borderColor: isChecked ? '#5D5FEF' : '#E5E7EB',
                                      backgroundColor: isChecked ? 'rgba(93, 95, 239, 0.04)' : '#FFFFFF',
                                      transition: 'all 0.15s ease-in-out',
                                      '&:hover': {
                                        borderColor: '#5D5FEF',
                                      },
                                    }}
                                  >
                                    <FormControlLabel
                                      control={
                                        <Checkbox
                                          size="small"
                                          checked={isChecked}
                                          onChange={() => togglePermission(item.id)}
                                          color="primary"
                                        />
                                      }
                                      label={
                                        <Box sx={{ ml: 0.5 }}>
                                          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.825rem' }}>
                                            {item.label}
                                          </Typography>
                                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.2, fontSize: '0.725rem' }}>
                                            {item.description}
                                          </Typography>
                                        </Box>
                                      }
                                      sx={{ m: 0, alignItems: 'flex-start' }}
                                    />
                                  </Paper>
                                </Grid>
                              );
                            })}
                          </Grid>
                        </Paper>
                      );
                    })}
                  </Stack>
                )}
              </Card>

              {/* Action Buttons */}
              <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mt: 3, mb: 4 }}>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => navigate('/dashboard/staff')}
                  sx={{ borderRadius: '8px', px: 4 }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={loading}
                  sx={{
                    borderRadius: '8px',
                    px: 5,
                    fontWeight: 700,
                    background: '#5D5FEF',
                    boxShadow: 'none',
                    '&:hover': { background: '#4B4DCC' },
                  }}
                >
                  {loading ? 'Saving Changes...' : 'Save Changes'}
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </form>

        {/* Revoke Access Confirmation Modal */}
        <Dialog
          open={confirmOpen}
          onClose={() => setConfirmOpen(false)}
          PaperProps={{
            sx: {
              borderRadius: '16px',
              padding: 1,
              maxWidth: 440,
              width: '100%',
            },
          }}
        >
          <DialogTitle sx={{ fontWeight: 700, pb: 1, display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '10px',
                bgcolor: '#FEE2E2',
                color: '#DC2626',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <DeleteIcon fontSize="small" />
            </Box>
            Revoke Staff Access
          </DialogTitle>
          <DialogContent sx={{ pt: 1 }}>
            <DialogContentText sx={{ color: 'text.secondary', fontSize: '0.925rem' }}>
              Are you sure you want to revoke staff access for{' '}
              <strong>{user?.username || user?.email}</strong>? This user will lose all staff permissions and administrative access.
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2, pt: 1 }}>
            <Button
              onClick={() => setConfirmOpen(false)}
              variant="outlined"
              sx={{ borderRadius: '8px', textTransform: 'none', px: 2.5 }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmRemove}
              color="error"
              variant="contained"
              disabled={deleting}
              sx={{ borderRadius: '8px', textTransform: 'none', px: 3, fontWeight: 600, background: '#DC2626' }}
            >
              {deleting ? 'Revoking...' : 'Yes, Revoke Access'}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Page>
  );
}
